/**
 * DualCTA engagement-gate A/B assignment (orthogonal to multi-Mux video A/B).
 *
 * Arms:
 *  - control: full teaser → onEnded → CTA (baseline)
 *  - gate_hotspot: pause at Mux hotspot (or fallback %) → membership CTA
 *
 * Priority: admin dual_cta_ops_config (runtime) → VITE_FF_* build override → defaults.
 * Session-stable via sessionStorage.
 */

export type EngagementGateArm = "control" | "gate_hotspot";

export type DualCtaOpsPublic = {
  engagement_gate_enabled?: boolean;
  engagement_gate_rollout_pct?: number;
  engagement_gate_copy_es?: string;
  engagement_gate_copy_en?: string;
  show_first_month_offer?: boolean;
  show_vip_price_badge?: boolean;
  exterior_default_promo_id?: string | null;
  /** Master kill: when true, all IAB conversion experiments OFF (public view already resolved). */
  iab_kill_switch?: boolean;
  iab_checkout_breakout_enabled?: boolean;
  iab_auto_mount_video_enabled?: boolean;
  iab_resume_banner_enabled?: boolean;
};

/**
 * Client-side effective IAB flags.
 * Fail-closed when ops is null (not loaded yet / fetch failed): experiments OFF
 * until admin config is known. Prevents a brief kill-switch bypass race and
 * accidental enable when the public API is down.
 * Server public view already applies kill → children OFF when loaded.
 */
export function isIabCheckoutBreakoutEnabled(
  ops?: DualCtaOpsPublic | null,
): boolean {
  if (!ops) return false;
  if (ops.iab_kill_switch === true) return false;
  return ops.iab_checkout_breakout_enabled !== false;
}

export function isIabAutoMountVideoEnabled(
  ops?: DualCtaOpsPublic | null,
): boolean {
  if (!ops) return false;
  if (ops.iab_kill_switch === true) return false;
  return ops.iab_auto_mount_video_enabled !== false;
}

export function isIabResumeBannerEnabled(
  ops?: DualCtaOpsPublic | null,
): boolean {
  if (!ops) return false;
  if (ops.iab_kill_switch === true) return false;
  return ops.iab_resume_banner_enabled !== false;
}

const STORAGE_PREFIX = "dual_cta_engagement_gate:";
let cachedOps: DualCtaOpsPublic | null = null;
let opsFetchPromise: Promise<DualCtaOpsPublic | null> | null = null;

function readEnvBool(name: string, defaultValue: boolean): boolean {
  try {
    const raw = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.[
      name
    ];
    if (raw === undefined || raw === "") return defaultValue;
    return raw === "true" || raw === "1";
  } catch {
    return defaultValue;
  }
}

function readEnvPct(name: string, defaultValue: number): number {
  try {
    const raw = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.[
      name
    ];
    if (raw === undefined || raw === "") return defaultValue;
    const n = Number.parseInt(String(raw), 10);
    if (!Number.isFinite(n)) return defaultValue;
    return Math.max(0, Math.min(100, n));
  } catch {
    return defaultValue;
  }
}

/** True if build env explicitly sets the FF (overrides DB). */
function envExplicitlySet(name: string): boolean {
  try {
    const raw = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.[
      name
    ];
    return raw !== undefined && raw !== "";
  } catch {
    return false;
  }
}

/** Fetch admin ops config (public). Cached in memory for the page session. */
export async function fetchDualCtaOpsPublic(): Promise<DualCtaOpsPublic | null> {
  if (cachedOps) return cachedOps;
  if (opsFetchPromise) return opsFetchPromise;
  opsFetchPromise = (async () => {
    try {
      const res = await fetch("/api/dual-cta-ops-config", {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return null;
      const body = (await res.json()) as DualCtaOpsPublic;
      cachedOps = body;
      return body;
    } catch {
      return null;
    } finally {
      opsFetchPromise = null;
    }
  })();
  return opsFetchPromise;
}

export function getCachedDualCtaOpsPublic(): DualCtaOpsPublic | null {
  return cachedOps;
}

/** Master kill switch. DB first, then env, default true. */
export function isEngagementGateMasterEnabled(
  ops?: DualCtaOpsPublic | null,
): boolean {
  if (envExplicitlySet("VITE_FF_ENGAGEMENT_GATE")) {
    return readEnvBool("VITE_FF_ENGAGEMENT_GATE", true);
  }
  if (ops && typeof ops.engagement_gate_enabled === "boolean") {
    return ops.engagement_gate_enabled;
  }
  return true;
}

/** % of sessions assigned to gate_hotspot (0–100). DB first, then env, default 0. */
export function getEngagementGateRolloutPct(
  ops?: DualCtaOpsPublic | null,
): number {
  if (envExplicitlySet("VITE_FF_ENGAGEMENT_GATE_ROLLOUT")) {
    return readEnvPct("VITE_FF_ENGAGEMENT_GATE_ROLLOUT", 0);
  }
  if (ops && typeof ops.engagement_gate_rollout_pct === "number") {
    return Math.max(0, Math.min(100, Math.round(ops.engagement_gate_rollout_pct)));
  }
  return 0;
}

function storageKey(slotOrPlayback: string): string {
  return `${STORAGE_PREFIX}${slotOrPlayback || "default"}`;
}

/**
 * Debug override: ?engagement_gate=control|gate_hotspot
 * Only for admin GeoSim / QA — does not write session if invalid.
 */
export function readEngagementGateDebugOverride(): EngagementGateArm | null {
  if (typeof window === "undefined") return null;
  try {
    const v = new URLSearchParams(window.location.search)
      .get("engagement_gate")
      ?.trim()
      .toLowerCase();
    if (v === "control" || v === "gate_hotspot") return v;
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Assign (or restore) engagement gate arm for this session.
 * Pass `ops` from /api/dual-cta-ops-config when available (admin-managed).
 */
export function assignEngagementGateArm(
  stickyKey = "dual_cta_landing",
  ops?: DualCtaOpsPublic | null,
): {
  arm: EngagementGateArm;
  source: "debug" | "session" | "fresh" | "master_off" | "rollout_0";
  rolloutPct: number;
} {
  const rolloutPct = getEngagementGateRolloutPct(ops ?? cachedOps);
  const master = isEngagementGateMasterEnabled(ops ?? cachedOps);

  const debug = readEngagementGateDebugOverride();
  if (debug) {
    return { arm: debug, source: "debug", rolloutPct };
  }

  if (!master) {
    return { arm: "control", source: "master_off", rolloutPct };
  }

  if (rolloutPct <= 0) {
    return { arm: "control", source: "rollout_0", rolloutPct };
  }

  if (typeof window === "undefined") {
    return { arm: "control", source: "fresh", rolloutPct };
  }

  const key = storageKey(stickyKey);
  try {
    const existing = sessionStorage.getItem(key);
    if (existing === "control" || existing === "gate_hotspot") {
      return { arm: existing, source: "session", rolloutPct };
    }
  } catch {
    /* private mode */
  }

  const roll = Math.random() * 100;
  const arm: EngagementGateArm = roll < rolloutPct ? "gate_hotspot" : "control";

  try {
    sessionStorage.setItem(key, arm);
  } catch {
    /* ignore */
  }

  return { arm, source: "fresh", rolloutPct };
}

export function clearEngagementGateAssignment(stickyKey = "dual_cta_landing"): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(storageKey(stickyKey));
  } catch {
    /* ignore */
  }
}
